package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.market.payroll.MonthPayFactors
import com.evoapps.evoshift.domain.market.payroll.MonthWorkInput
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.employee_created
import evoshift.composeapp.generated.resources.employee_added_from_other_workplace
import evoshift.composeapp.generated.resources.employee_email_required
import evoshift.composeapp.generated.resources.employee_invite_sent
import evoshift.composeapp.generated.resources.employee_name_required
import evoshift.composeapp.generated.resources.employee_removed
import evoshift.composeapp.generated.resources.employee_updated
import evoshift.composeapp.generated.resources.error_send_failed
import evoshift.composeapp.generated.resources.error_session_expired
import evoshift.composeapp.generated.resources.saves_in_progress
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.CompensationModel
import com.evoapps.evoshift.domain.model.FinancialEngagement
import com.evoapps.evoshift.domain.model.sameTermsAs
import com.evoapps.evoshift.domain.model.TermsChange
import com.evoapps.evoshift.domain.model.ContractType
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.model.mayAssign
import com.evoapps.evoshift.domain.billing.PlanCheck
import com.evoapps.evoshift.domain.usecase.billing.CheckSeatsUseCase
import com.evoapps.evoshift.domain.usecase.GetWorkplaceUseCase
import com.evoapps.evoshift.domain.usecase.SendOnboardingInviteUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.user.CreateEmployeeUseCase
import com.evoapps.evoshift.domain.usecase.user.newFinancialEngagement
import com.evoapps.evoshift.domain.usecase.AddEngagementUseCase
import com.evoapps.evoshift.domain.usecase.team.GetPeopleFromOtherWorkplacesUseCase
import com.evoapps.evoshift.domain.usecase.team.OtherWorkplacePerson
import com.evoapps.evoshift.domain.usecase.user.GetEmployeeDetailsUseCase
import com.evoapps.evoshift.domain.usecase.user.TerminateEngagementUseCase
import com.evoapps.evoshift.domain.usecase.user.UpdateEmployeeUseCase
import com.evoapps.evoshift.domain.market.Market
import com.evoapps.evoshift.domain.market.payroll.PayrollProfile
import com.evoapps.evoshift.domain.market.payroll.PayrollRules
import com.evoapps.evoshift.domain.market.payroll.Payslip
import com.evoapps.evoshift.domain.market.payroll.PayslipInput
import com.evoapps.evoshift.domain.market.payroll.WorkplacePayrollSettings
import com.evoapps.evoshift.domain.model.SystemContractType
import com.evoapps.evoshift.domain.usecase.finance.RepriceFutureShiftsUseCase
import kotlinx.datetime.DatePeriod
import com.evoapps.evoshift.saves.PendingSaves
import com.evoapps.evoshift.saves.awaitOrKeepOnDevice
import com.evoapps.evoshift.saves.fold
import com.evoapps.evoshift.saves.runOrKeepOnDevice
import com.evoapps.evoshift.saves.SaveKind
import kotlinx.datetime.TimeZone
import kotlinx.datetime.plus
import kotlinx.datetime.number
import kotlinx.datetime.toLocalDateTime
import kotlin.math.roundToInt
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import com.evoapps.evoshift.domain.model.Competence
import com.evoapps.evoshift.domain.model.InvalidStaffRecordException
import com.evoapps.evoshift.domain.model.StaffChecks
import com.evoapps.evoshift.domain.model.StaffReadiness
import com.evoapps.evoshift.domain.model.StaffRecord
import com.evoapps.evoshift.domain.model.StaffRecordProblem
import com.evoapps.evoshift.domain.model.problems
import com.evoapps.evoshift.domain.usecase.staff.GetStaffDetailsUseCase
import com.evoapps.evoshift.domain.usecase.staff.SaveAvailabilityNotesUseCase
import com.evoapps.evoshift.domain.usecase.staff.SaveStaffRecordUseCase
import com.evoapps.evoshift.observability.AnalyticsEvent
import com.evoapps.evoshift.observability.AppTelemetry
import com.evoapps.evoshift.widgets.parseIsoDate
import evoshift.composeapp.generated.resources.staff_checks_saved
import evoshift.composeapp.generated.resources.staff_notes_saved
import evoshift.composeapp.generated.resources.staff_ohs_valid_before_training
import kotlinx.datetime.LocalDate
import kotlinx.datetime.todayIn

data class EmployeeFormUiState(
    /** Only the first load of the form; saving keeps the form on screen ([isSaving]). */
    val isLoading: Boolean = true,
    /** A save, removal or invitation is on its way: its button is disabled and shows progress. */
    val isSaving: Boolean = false,
    val isEditMode: Boolean = false,
    /** The person being edited (null: a new one). */
    val employeeId: String? = null,
    val error: UiText? = null,
    val success: Boolean = false,
    val message: UiText? = null,

    // Form Fields
    val name: String = "",
    val role: String = "",
    val email: String = "",
    val permission: UserPermission = UserPermission.EMPLOYEE,
    val skills: List<String> = emptyList(), // Changed to List<String> for multi-select support
    val isPrimary: Boolean = true,
    /** A [SystemContractType] name (older documents: free text, read through fromId). */
    val contractType: String = "",
    val payrollProfile: PayrollProfile = PayrollProfile(),
    val declaredNumberOfHours: String = "", // Added for Zlecenie
    /** SH-117: the number in the accountant's payroll software / RCP card, digits only (blank: none). */
    val payrollId: String = "",
    val phoneNumber: String = "",
    val address: String = "",
    val bankAccountNumber: String = "",
    val isHourly: Boolean = true,
    val rate: String = "",
    /** What this pay comes to in a month under the workplace market's rules (null without a rate). */
    val estimate: Payslip? = null,
    val estimateHours: Int = 0,
    /**
     * SH-57: the minimum wage against this pay: the top-up an employment contract would get (in
     * [estimate]'s gross) or a civil-law rate under the hourly minimum (a warning).
     */
    val estimateFactors: MonthPayFactors? = null,
    val currency: String = "PLN",
    val baseSalary: String = "",
    val fte: String = "1.0",
    /** SH-57: the stored pay of the person being edited (null when new or not readable). */
    val loadedPay: CompensationModel? = null,
    /**
     * SH-250: the day ("YYYY-MM-DD") the stored pay applies from (from the start: the hire, when
     * known); null: not known or nothing stored.
     */
    val paySince: String? = null,
    /** SH-250: the next pay change already planned (from a later day), when one. */
    val nextPay: com.evoapps.evoshift.domain.model.RatePeriod? = null,
    /**
     * SH-57: the day ("YYYY-MM-DD") changed pay applies from; earlier days keep the stored pay.
     * SH-149: the same day for payroll facts changed together with it (one date for the change).
     */
    val rateEffectiveFrom: String = "",
    /** SH-149: the stored payroll facts of the person being edited (null when new or not readable). */
    val loadedProfile: PayrollProfile? = null,
    /** SH-149: the dated changes of the pay and the payroll facts, newest first. */
    val termsHistory: List<TermsChange> = emptyList(),
    val shouldSendInvite: Boolean = false,

    // SH-47 staff fields. Competence belongs to the engagement (saved with it).
    val competence: Competence = Competence.INDEPENDENT,
    /** The dates as typed ("YYYY-MM-DD", blank: not recorded); only for [showsStaffDates]. */
    val ohsTrainingDate: String = "",
    val ohsValidUntil: String = "",
    val medicalValidUntil: String = "",
    /** The statuses: from the dates for those who read them, else from the status document. */
    val readiness: StaffReadiness? = null,
    /** Availability notes as typed. */
    val notes: String = "",
    val isShadow: Boolean = true, // Default to true (Shadow) for new users
    
    val showInviteDialogAfterCreation: Boolean = false,
    /** The join code of the invitation just created, shown so the manager can pass it on. */
    val inviteCode: String? = null,
    /** Why creating the invitation failed, shown inside the invitation dialog (a snackbar would sit under it). */
    val inviteError: UiText? = null,
    val createdUserId: String? = null,
    /** The owner's plan allows no more team members here: the screen offers the plans (SH-38). */
    val planLimit: PlanCheck.LimitReached? = null,
    /** Whether whoever hit [planLimit] may choose a plan (the owner); others ask the owner. */
    val planLimitCanChoosePlan: Boolean = false,

    // Helpers
    val availableRoles: List<String> = emptyList(),
    val availableSkills: List<String> = emptyList(), // Suggestions from WorkplaceSettings
    val availableContractTypes: List<ContractType> = emptyList(),
    /** The permissions the viewer may give: none above their own (firestore.rules). */
    val availablePermissions: List<UserPermission> = emptyList(),

    // What the viewer may do (Capability): the form shows only that.
    /** The viewer's capabilities at this workplace. */
    val capabilities: Set<Capability> = emptySet(),
    /**
     * The viewer may change this person (save, remove, invite): they manage the team, the person
     * is not above them, and it is not their own engagement (only the owner changes their own).
     */
    val canEdit: Boolean = false,
    /**
     * SH-147: the person is confirmed as working here without pay (0 zł): their shifts cost
     * nothing and the missing-pay warning leaves them out. Not offered for the workplace's owner
     * ([isWorkplaceOwner]), whose missing pay never warns (the workplace settings may mark their
     * shifts unpaid).
     */
    val worksUnpaid: Boolean = false,
    val isWorkplaceOwner: Boolean = false,
    /**
     * SH-255: "Hide in the roster for others": colleagues do not see this person's shifts. Null
     * until read, or for a viewer who does not manage the team (not shown then).
     */
    val hiddenInRoster: Boolean? = null,
    /**
     * SH-206: "Count in labour cost and SPMH" this month (the owner too); null until read, or for
     * a viewer who may not see it (only owners and financial managers do).
     */
    val labourInclusion: com.evoapps.evoshift.domain.budget.LabourInclusionStatus? = null,
    /**
     * SH-192: the active people of the viewer's other workplaces (those they manage) who do not
     * work here in any state, offered by "From your other workplaces" when adding someone.
     */
    val otherWorkplacePeople: List<OtherWorkplacePerson> = emptyList(),
    /**
     * SH-192: the person picked from another workplace: the form adds this workplace to their
     * profile (role, permission, pay terms) instead of creating someone new; their name, other
     * engagements and contact details stay theirs.
     */
    val pickedPerson: OtherWorkplacePerson? = null
) {
    /** SH-192: "From your other workplaces" while adding someone (not on a read-only plan). */
    val showsFromOtherWorkplaces: Boolean get() = !isEditMode && canEdit && otherWorkplacePeople.isNotEmpty()

    /** SH-192: the private data section: not for someone from another workplace (their contact details are theirs). */
    val showsPrivateData: Boolean get() = showsPayAndPrivateData && pickedPerson == null

    /** Pay terms and personal data: shown, loaded and saved only with MANAGE_PAY. */
    val showsPayAndPrivateData: Boolean get() = capabilities.shows(Gate.PAY_AND_PRIVATE_DATA)

    /** SH-147: "Works without pay (0 zł)" for an existing person other than the owner (owners, financial managers). */
    val showsWorksUnpaid: Boolean get() = isEditMode && employeeId != null && !isWorkplaceOwner && capabilities.shows(Gate.WORKPLACE_FINANCES)
    val canChangeWorksUnpaid: Boolean get() = showsWorksUnpaid && capabilities.shows(Gate.WORKPLACE_FINANCES_EDITING)

    /** SH-206: "Count in labour cost and SPMH" for an existing person, the owner too (owners, financial managers). */
    val showsLabourInclusion: Boolean get() =
        isEditMode && employeeId != null && labourInclusion != null && capabilities.shows(Gate.WORKPLACE_FINANCES)
    val canChangeLabourInclusion: Boolean get() = showsLabourInclusion && capabilities.shows(Gate.WORKPLACE_FINANCES_EDITING)

    /** SH-255: "Hide in the roster for others" for an existing person (managers, read). */
    val showsHiddenInRoster: Boolean get() = isEditMode && employeeId != null && hiddenInRoster != null && capabilities.shows(Gate.TEAM)
    /** SH-255: switched by those who may change this person (not once the plan ended). */
    val canChangeHiddenInRoster: Boolean get() = showsHiddenInRoster && canEdit && capabilities.shows(Gate.TEAM_EDITING)

    /** SH-47: an existing person's statuses and notes (managers). */
    val showsStaffStatus: Boolean get() = isEditMode && capabilities.shows(Gate.STAFF_STATUS)
    /** SH-47: the medical exam / OHS dates themselves (owners, financial managers). */
    val showsStaffDates: Boolean get() = isEditMode && capabilities.shows(Gate.STAFF_RECORDS)
    /** SH-212: the person's timeline (owners, financial managers). */
    val showsTimeline: Boolean get() = isEditMode && employeeId != null && capabilities.shows(Gate.EMPLOYEE_TIMELINE)
    /** SH-122: the person's documents (owners, financial managers). */
    val showsDocuments: Boolean get() = isEditMode && employeeId != null && capabilities.shows(Gate.STAFF_DOCUMENTS)
    val canEditStaffDates: Boolean get() = canEdit && showsStaffDates && capabilities.shows(Gate.STAFF_RECORDS_EDITING)
    val canEditNotes: Boolean get() = canEdit && showsStaffStatus && capabilities.shows(Gate.STAFF_NOTES_EDITING)

    /** The dates as typed, or null while a field does not hold a day (the field shows why). */
    val typedStaffRecord: StaffRecord?
        get() {
            val days = listOf(ohsTrainingDate, ohsValidUntil, medicalValidUntil).map { it.trim() }
            if (days.any { it.isNotEmpty() && parseIsoDate(it) == null }) return null
            val (training, ohs, medical) = days.map { it.takeIf(String::isNotEmpty)?.let(::parseIsoDate) }
            return StaffRecord(userId = "", workplaceId = "", ohsTrainingDate = training, ohsValidUntil = ohs, medicalExamValidUntil = medical)
        }

    /** The dates' problem shown in the dialog (a training valid until before it happened). */
    val staffDatesProblem: StaffRecordProblem? get() = typedStaffRecord?.problems()?.firstOrNull()

    val contract: SystemContractType get() = SystemContractType.fromId(contractType)

    /** The pay as typed. */
    val typedPay: CompensationModel
        get() = if (isHourly) CompensationModel.Hourly(rate.toDoubleOrNull() ?: 0.0, 0.0)
        else CompensationModel.MonthlyFixed(baseSalary.toDoubleOrNull() ?: 0.0, 0.0, fte.toDoubleOrNull() ?: 1.0)

    /** SH-57: an existing person's pay was changed: the form asks from which day it applies. */
    val payChanged: Boolean get() = isEditMode && loadedPay != null && !typedPay.sameTermsAs(loadedPay)

    /** SH-149: an existing person's payroll facts were changed (not only a known end): dated like pay. */
    val profileChanged: Boolean get() = isEditMode && loadedProfile != null && !payrollProfile.sameFactsAs(loadedProfile)

    /** SH-149: the form asks from which day the changed pay and facts apply. */
    val termsChanged: Boolean get() = payChanged || profileChanged

    /**
     * SH-149: the first day priced differently because a known end (under 26, student) was set,
     * moved or removed: the day after the earlier of the old and the new end. Null: no end changed.
     */
    val knownEndChangedFrom: LocalDate?
        get() {
            val before = loadedProfile ?: return null
            return listOf(before.under26Until to payrollProfile.under26Until, before.studentUntil to payrollProfile.studentUntil)
                .filter { (old, new) -> old.orEmpty() != new.orEmpty() }
                .flatMap { (old, new) -> listOfNotNull(old, new) }
                .mapNotNull { parseIsoDate(it)?.plus(DatePeriod(days = 1)) }
                .minOrNull()
        }

    /** SH-149: a known end typed that is not a day (the dialog cannot be saved). */
    val knownEndInvalid: Boolean
        get() = listOfNotNull(payrollProfile.under26Until, payrollProfile.studentUntil).any { it.isNotBlank() && parseIsoDate(it.trim()) == null }
}

/** SH-117: the longest payroll ID the RCP files take (Symfonia and Optima: 10 digits). */
const val PAYROLL_ID_MAX_DIGITS = 10

sealed interface EmployeeFormEvent {
    data class UpdateName(val value: String) : EmployeeFormEvent
    data class UpdateRole(val value: String) : EmployeeFormEvent
    data class UpdateEmail(val value: String) : EmployeeFormEvent
    data class UpdatePermission(val value: UserPermission) : EmployeeFormEvent
    data class UpdateSkills(val value: List<String>) : EmployeeFormEvent
    data class ToggleSkill(val skill: String) : EmployeeFormEvent // Helper for checkboxes
    data class UpdateIsPrimary(val value: Boolean) : EmployeeFormEvent
    data class UpdateContractType(val value: String) : EmployeeFormEvent
    data class UpdateDeclaredNumberOfHours(val value: String) : EmployeeFormEvent
    data class UpdatePayrollId(val value: String) : EmployeeFormEvent
    data class UpdatePhoneNumber(val value: String) : EmployeeFormEvent
    data class UpdateAddress(val value: String) : EmployeeFormEvent
    data class UpdateBankAccount(val value: String) : EmployeeFormEvent
    data class UpdateIsHourly(val value: Boolean) : EmployeeFormEvent
    data class UpdateRate(val value: String) : EmployeeFormEvent
    data class UpdateBaseSalary(val value: String) : EmployeeFormEvent
    data class UpdatePayrollProfile(val value: PayrollProfile) : EmployeeFormEvent
    data class UpdateFte(val value: String) : EmployeeFormEvent
    /** SH-57: the day changed pay applies from. */
    data class UpdateRateEffectiveFrom(val value: String) : EmployeeFormEvent
    data class UpdateShouldSendInvite(val value: Boolean) : EmployeeFormEvent
    /** SH-147: confirms (true) or undoes (false) that the person works here without pay; saved at once. */
    data class SetWorksUnpaid(val value: Boolean) : EmployeeFormEvent
    /** SH-206: "Count in labour cost and SPMH" switched. */
    data class SetCountsInLabour(val value: Boolean) : EmployeeFormEvent
    /** SH-255: "Hide in the roster for others" switched; saved at once. */
    data class SetHiddenInRoster(val value: Boolean) : EmployeeFormEvent
    // SH-47
    data class UpdateCompetence(val value: Competence) : EmployeeFormEvent
    data class UpdateOhsTrainingDate(val value: String) : EmployeeFormEvent
    data class UpdateOhsValidUntil(val value: String) : EmployeeFormEvent
    data class UpdateMedicalValidUntil(val value: String) : EmployeeFormEvent
    data class UpdateNotes(val value: String) : EmployeeFormEvent
    /** Saves the medical exam / OHS dates (their own dialog, optimistic). */
    data object SaveStaffChecks : EmployeeFormEvent
    /** Saves the availability notes (their own dialog, optimistic). */
    data object SaveNotes : EmployeeFormEvent
    data object Submit : EmployeeFormEvent
    data object DismissInviteCode : EmployeeFormEvent
    /** An invitation without email: the manager copies the code and passes it on. */
    data object CreateInviteCode : EmployeeFormEvent
    data object SendInvite : EmployeeFormEvent
    data object DismissInviteDialog : EmployeeFormEvent
    /** An existing employee: open the invitation options (e-mail or code) without saving the form. */
    data object ShowInviteOptions : EmployeeFormEvent
    data object RemoveEmployee : EmployeeFormEvent
    data object ClearMessage : EmployeeFormEvent
    /** A section dialog opens: the form is remembered so that cancelling can put it back. */
    data object BeginSectionEdit : EmployeeFormEvent
    /** The section dialog was cancelled: the form goes back to how it was when it opened. */
    data object CancelSectionEdit : EmployeeFormEvent
    data object PlanLimitShown : EmployeeFormEvent
    /** SH-192: the person [userId] of another workplace is the one to add here. */
    data class PickPersonFromOtherWorkplace(val userId: String) : EmployeeFormEvent
    /** SH-192: back to adding someone new. */
    data object ClearPickedPerson : EmployeeFormEvent
}

class EmployeeFormViewModel(
    private val workplaceId: String,
    private val employeeId: String?, // Null = Create Mode
    private val createEmployeeUseCase: CreateEmployeeUseCase,
    private val updateEmployeeUseCase: UpdateEmployeeUseCase,
    private val getEmployeeDetailsUseCase: GetEmployeeDetailsUseCase,
    private val terminateEngagementUseCase: TerminateEngagementUseCase,
    private val getWorkplaceUseCase: GetWorkplaceUseCase,
    private val sendInviteUseCase: SendOnboardingInviteUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    /**
     * SH-57 / SH-148: reprices the shifts from a day on after a pay change
     * ([RepriceFutureShiftsUseCase]); run in the background after the save, not awaited by it.
     */
    private val repriceFutureShifts: (suspend (workplaceId: String, from: LocalDate?) -> Result<Int>)? = null,
    private val checkSeats: CheckSeatsUseCase? = null,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    /** SH-47 staff fields; without them the form shows none. */
    private val getStaffDetails: GetStaffDetailsUseCase? = null,
    private val saveStaffRecord: SaveStaffRecordUseCase? = null,
    private val saveAvailabilityNotes: SaveAvailabilityNotesUseCase? = null,
    /** SH-125 "Hire": (job post id, application id) to fill a new person in from; marked hired once created. */
    private val applicant: Pair<String, String>? = null,
    private val hiringRepository: com.evoapps.evoshift.domain.repository.HiringRepository? = null,
    private val markApplicantHired: com.evoapps.evoshift.domain.usecase.hiring.MarkApplicantHiredUseCase? = null,
    /** SH-147: "Works without pay (0 zł)"; without it the switch is not offered. */
    private val setWorksUnpaid: com.evoapps.evoshift.domain.budget.SetWorksUnpaidUseCase? = null,
    /** SH-147: tells the labour cost screens that pay terms changed. */
    private val payTermsChanges: com.evoapps.evoshift.domain.budget.PayTermsChanges? = null,
    /** SH-206: "Count in labour cost and SPMH"; without both it is not offered. */
    private val getLabourInclusion: com.evoapps.evoshift.domain.budget.GetLabourInclusionUseCase? = null,
    private val setLabourInclusion: com.evoapps.evoshift.domain.budget.SetLabourInclusionUseCase? = null,
    private val today: () -> LocalDate = { AppClock.todayIn(TimeZone.currentSystemDefault()) },
    /** SH-148: the app's saves (a singleton): a save outlives this view model and the screen. */
    private val saves: PendingSaves = PendingSaves(),
    /** SH-186: offline, a save unanswered for a few seconds counts as kept on this device. */
    private val connectivity: com.evoapps.evoshift.connectivity.ConnectivityWatcher = com.evoapps.evoshift.connectivity.ManualConnectivity(),
    /** SH-192: "From your other workplaces"; without both it is not offered. */
    private val getPeopleFromOtherWorkplaces: GetPeopleFromOtherWorkplacesUseCase? = null,
    private val addEngagement: AddEngagementUseCase? = null,
    /**
     * SH-250: the person's pay terms here as they change (UserRepository.observeFinancialEngagement):
     * a change saved elsewhere (the employee timeline) shows at once. Without it: read on opening.
     */
    private val observePayTerms: ((userId: String, workplaceId: String) -> Flow<FinancialEngagement?>)? = null,
    /** SH-255: "Hide in the roster for others"; without both it is not offered. */
    private val getHiddenInRoster: com.evoapps.evoshift.domain.privacy.GetHiddenInRosterUseCase? = null,
    private val setHiddenInRoster: com.evoapps.evoshift.domain.privacy.SetHiddenInRosterUseCase? = null
) : ViewModel() {

    /** SH-148: what this form saves, so a form opened again during the save finds it. */
    private val saveKey = employeeSaveKey(workplaceId, employeeId)

    /**
     * Someone new takes a seat of the owner's plan (SH-52), counted across all the owner's
     * workplaces; checked for owners and managers alike. Null: there is room.
     */
    private suspend fun planLimitForNewEmployee(adding: Int = 1): PlanCheck.LimitReached? =
        checkSeats?.invoke(workplaceId, adding) as? PlanCheck.LimitReached

    private var market: Market = Market.POLAND

    /** The form as it was when a section dialog opened ([EmployeeFormEvent.BeginSectionEdit]). */
    private var sectionSnapshot: EmployeeFormUiState? = null
    private var payrollSettings = WorkplacePayrollSettings()
    /** SH-57: the pay terms as loaded, with their history (kept when saving). */
    private var loadedFinancial: FinancialEngagement? = null
    /** SH-250: the hire day here ("YYYY-MM-DD"), the start of pay applying "from the start". */
    private var hireDay: String? = null
    /** SH-250: the live pay terms are followed once per form. */
    private var payTermsJob: Job? = null

    /** SH-250: these fields showing [financial] (the stored pay terms), as on opening. */
    private fun EmployeeFormUiState.withPayTerms(financial: FinancialEngagement?): EmployeeFormUiState {
        val outlook = financial?.payOutlook(today().toString())
        return copy(
            contractType = financial?.contract?.takeIf { c -> c != SystemContractType.NOT_SET }?.name ?: "",
            payrollProfile = financial?.payrollProfile ?: PayrollProfile(),
            declaredNumberOfHours = financial?.declaredNumberOfHours?.toString() ?: "",
            payrollId = financial?.payrollId.orEmpty(),
            isHourly = financial?.compensation is CompensationModel.Hourly,
            rate = when (val comp = financial?.compensation) {
                is CompensationModel.Hourly -> comp.rate.toString()
                else -> ""
            },
            baseSalary = when (val comp = financial?.compensation) {
                is CompensationModel.MonthlyFixed -> comp.baseSalary.toString()
                else -> ""
            },
            fte = (financial?.compensation as? CompensationModel.MonthlyFixed)?.fte?.toString() ?: "1.0",
            loadedPay = financial?.compensation,
            loadedProfile = financial?.payrollProfile,
            termsHistory = financial?.termsChanges.orEmpty().reversed(),
            paySince = outlook?.let { it.since ?: hireDay },
            nextPay = outlook?.next,
            rateEffectiveFrom = ""
        )
    }

    /**
     * SH-250: follows the stored pay terms: a change made elsewhere (the timeline) replaces the
     * shown pay at once. Not while this form saves (its own values are on screen, and a refused
     * save keeps them for another try) nor over a section being edited: the edit then starts from
     * the new terms when cancelled. The terms a save builds on are always the newest.
     */
    private fun followPayTerms(userId: String) {
        val observe = observePayTerms ?: return
        if (payTermsJob != null) return
        payTermsJob = viewModelScope.launch {
            observe(userId, workplaceId).distinctUntilChanged().collect { financial ->
                if (financial == null || financial == loadedFinancial) return@collect
                loadedFinancial = financial
                val editing = sectionSnapshot
                when {
                    _uiState.value.isSaving -> Unit
                    editing != null -> {
                        val fresh = editing.withPayTerms(financial).withEstimate()
                        sectionSnapshot = fresh
                        // What is typed stays; what it is compared with (and shown beside it) is new.
                        _uiState.update {
                            it.copy(
                                loadedPay = fresh.loadedPay, loadedProfile = fresh.loadedProfile, termsHistory = fresh.termsHistory,
                                paySince = fresh.paySince, nextPay = fresh.nextPay
                            ).withPayDate()
                        }
                    }
                    else -> _uiState.update { it.withPayTerms(financial).withEstimate() }
                }
            }
        }
    }

    /** SH-57: changed pay (SH-149: or payroll facts) applies from today unless another day is picked. */
    private fun EmployeeFormUiState.withPayDate(): EmployeeFormUiState = when {
        !termsChanged -> copy(rateEffectiveFrom = "")
        rateEffectiveFrom.isBlank() -> copy(rateEffectiveFrom = today().toString())
        else -> this
    }

    /** The month's gross, net and employer cost for the pay being edited (no allowances). */
    private fun EmployeeFormUiState.withEstimate(): EmployeeFormUiState {
        val rules = PayrollRules.of(market)
        val today = AppClock.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
        val norm = rules.workingHours(today.year, today.month.number)
        val (gross, hours) = if (isHourly) {
            val hours = declaredNumberOfHours.toIntOrNull() ?: norm
            (rate.toDoubleOrNull() ?: 0.0) * hours to hours
        } else {
            (baseSalary.toDoubleOrNull() ?: 0.0) to (norm * (fte.toDoubleOrNull() ?: 1.0)).roundToInt()
        }
        if (gross <= 0) return copy(estimate = null, estimateHours = 0, estimateFactors = null)
        val compensation = if (isHourly) CompensationModel.Hourly(rate.toDoubleOrNull() ?: 0.0, 0.0)
        else CompensationModel.MonthlyFixed(baseSalary.toDoubleOrNull() ?: 0.0, 0.0, fte.toDoubleOrNull() ?: 1.0)
        val factors = rules.monthFactors(
            MonthWorkInput(
                today.year, today.month.number, contract, compensation, shifts = emptyList(), settings = payrollSettings,
                assumedHours = hours.toDouble()
            )
        )
        val slip = rules.payslip(PayslipInput(today.year, today.month.number, contract, payrollProfile, gross + factors.minimumWageTopUp, payrollSettings))
        return copy(estimate = slip, estimateHours = hours, estimateFactors = factors.takeIf { it.minimumWageTopUp > 0 || it.belowMinimumHourlyRate })
    }

    private val _uiState = MutableStateFlow(
        EmployeeFormUiState(
            isEditMode = employeeId != null,
            employeeId = employeeId,

        )
    )
    val uiState = _uiState.asStateFlow()

    init {
        loadData()
    }

    /** [quiet]: a re-read after a save the form followed; the form stays on screen. */
    private fun loadData(quiet: Boolean = false) {
        viewModelScope.launch {
            if (!quiet) _uiState.update { it.copy(isLoading = true) }

            // 0. Who is looking: what they may see and change here.
            val me = getCurrentUserUseCase().first()
            val myPermission = me?.engagements?.get(workplaceId)?.takeIf { it.isActive }?.permission
            val capabilities = me?.capabilitiesAt(workplaceId, observePlanActive.activeNow(workplaceId)).orEmpty()
            // Changing the team: not once the workplace's plan ended (read-only, SH-65).
            val mayManage = capabilities.shows(Gate.TEAM_EDITING) && myPermission != null
            _uiState.update {
                it.copy(
                    capabilities = capabilities,
                    availablePermissions = UserPermission.entries.filter { p -> myPermission?.mayAssign(p) == true },
                    // A new person: anyone who manages the team adds them.
                    canEdit = employeeId == null && mayManage
                )
            }

            // 1. Load Workplace Settings
            val workplaceResult = getWorkplaceUseCase(workplaceId)
            val workplace = workplaceResult.getOrNull()
            val roles = workplace?.settings?.availableRoles ?: emptyList()
            val workplaceSkills = workplace?.settings?.workplaceSkills ?: emptyList()
            val contractTypes = workplace?.settings?.contractTypes ?: emptyList()
            workplace?.settings?.let { settings ->
                market = settings.market
                payrollSettings = settings.payroll
                _uiState.update {
                    it.copy(
                        currency = settings.currency,
                        worksUnpaid = employeeId != null && workplace.worksUnpaidOn(employeeId, today().toString()),
                        isWorkplaceOwner = employeeId != null && employeeId == workplace.ownerId
                    )
                }
            }

            if (employeeId != null) {
                // Edit Mode: Load Employee Data
                // Pay and personal data only for those who may read them (nothing is asked otherwise).
                getEmployeeDetailsUseCase(employeeId, includeSensitive = capabilities.shows(Gate.PAY_AND_PRIVATE_DATA)).fold(
                    onSuccess = { details ->
                        val user = details.user
                        val engagement = user.engagements[workplaceId]
                        val sensitive = details.sensitiveData
                        val financial = sensitive?.financialEngagements?.get(workplaceId)
                        loadedFinancial = financial
                        hireDay = engagement?.startDate?.take(10)?.takeIf { d -> parseIsoDate(d) != null }

                        _uiState.update {
                            it.withPayTerms(financial).copy(
                                isLoading = false,
                                availableRoles = roles,
                                availableSkills = workplaceSkills,
                                availableContractTypes = contractTypes,
                                name = user.displayName,
                                role = engagement?.role ?: "",
                                email = sensitive?.email ?: "", // Populate email from sensitive data
                                permission = engagement?.permission ?: UserPermission.EMPLOYEE,
                                skills = engagement?.skills ?: emptyList(),
                                isPrimary = engagement?.isPrimary ?: true,
                                competence = engagement?.competence ?: Competence.INDEPENDENT,
                                phoneNumber = sensitive?.phoneNumber ?: "",
                                address = sensitive?.address ?: "",
                                bankAccountNumber = sensitive?.bankAccountNumber ?: "",
                                isShadow = user.isShadow,
                                // Another owner's OWNER engagement is only theirs to change (rules).
                                canEdit = mayManage && engagement != null &&
                                    myPermission.mayAssign(engagement.permission) &&
                                    (employeeId != me.uid || myPermission == UserPermission.OWNER) &&
                                    !(engagement.permission == UserPermission.OWNER && employeeId != me.uid)
                            ).withEstimate()
                        }
                        if (!quiet) followPendingSave()
                        if (capabilities.shows(Gate.PAY_AND_PRIVATE_DATA)) followPayTerms(employeeId)
                        loadStaffDetails(employeeId)
                        if (capabilities.shows(Gate.WORKPLACE_FINANCES)) loadLabourInclusion(employeeId)
                        if (capabilities.shows(Gate.TEAM)) loadHiddenInRoster(employeeId)
                    },
                    onFailure = { e ->
                        _uiState.update { it.copy(isLoading = false, error = e.toUiText()) }
                    }
                )
            } else {
                // Create Mode
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        availableRoles = roles,
                        availableSkills = workplaceSkills,
                        availableContractTypes = contractTypes,
                        isShadow = true // New employees created by manager are shadows by default
                    )
                }
                prefillFromApplicant()
                if (mayManage && applicant == null && me != null) loadOtherWorkplacePeople(me)
            }
        }
    }

    /** SH-192: who of the viewer's other workplaces could be added here (best effort: none offered on failure). */
    private suspend fun loadOtherWorkplacePeople(me: com.evoapps.evoshift.domain.model.User) {
        val use = getPeopleFromOtherWorkplaces ?: return
        if (addEngagement == null) return
        val people = use(me, workplaceId).getOrNull().orEmpty()
        _uiState.update { it.copy(otherWorkplacePeople = people) }
    }

    /**
     * SH-192: the form becomes the adding of [userId]: their name (not editable), their role at
     * the other workplace when this one has it, not their primary workplace (they have one).
     */
    private fun pickPerson(userId: String) {
        val state = _uiState.value
        if (!state.showsFromOtherWorkplaces || state.isSaving) return
        val person = state.otherWorkplacePeople.firstOrNull { it.user.uid == userId } ?: return
        _uiState.update {
            it.copy(
                pickedPerson = person,
                name = person.user.displayName,
                role = person.role?.takeIf { r -> r in it.availableRoles } ?: it.role,
                isPrimary = false,
                email = "", phoneNumber = "", address = "", bankAccountNumber = ""
            )
        }
    }

    private fun clearPickedPerson() {
        if (_uiState.value.pickedPerson == null || _uiState.value.isSaving) return
        _uiState.update { it.copy(pickedPerson = null, name = "", role = "", isPrimary = true) }
    }

    /**
     * SH-148: the form opened again while its save still runs (or after it failed unseen): it
     * shows the values being saved, not the stored ones, and the save's progress; a second
     * submit waits for it. When the save ends the form re-reads the stored person (and stays).
     */
    private fun followPendingSave() {
        val pending = saves.entry(saveKey) ?: return
        val submitted = pending.payload as? EmployeeFormUiState
        if (submitted != null) _uiState.update { it.withFormOf(submitted) }
        if (!pending.isRunning) {
            // A failed save: its values stay here for another try, and the form says why.
            saves.dismiss(pending.id)
            _uiState.update { it.copy(error = pending.failure) }
            return
        }
        _uiState.update { it.copy(isSaving = true) }
        viewModelScope.launch {
            saves.awaitOrKeepOnDevice(connectivity.isOnline) { saves.await<Unit>(saveKey) ?: Result.success(Unit) }.fold(
                onSuccess = {
                    _uiState.update { it.copy(isSaving = false, message = UiText.Resource(Res.string.employee_updated)) }
                    loadData(quiet = true)
                },
                onFailure = { e -> _uiState.update { it.copy(isSaving = false, error = e.toUiText()) } },
                onKept = { _uiState.update { it.copy(isSaving = false) } }
            )
        }
    }

    /**
     * SH-148: after a pay save, the workplace's shifts from [from] (today when null) on are
     * repriced in the background ("Recalculating costs…"); queued behind one still running, so
     * the last pay always wins. A failure is reported (non-fatal) and offered again with Retry.
     */
    private fun startRepricing(from: LocalDate?) {
        val reprice = repriceFutureShifts ?: return
        // SH-147: the missing-pay warnings and labour cost screens read again once the pay is saved
        // and again when the costs are repriced.
        payTermsChanges?.changed()
        saves.launch(repriceSaveKey(workplaceId), SaveKind.RECALCULATE, queue = true) {
            reprice(workplaceId, from).also { payTermsChanges?.changed() }
        }
    }

    /** SH-125: a new person from an applicant: their name, contact and the post's role. */
    private suspend fun prefillFromApplicant() {
        val (postId, applicationId) = applicant ?: return
        val repository = hiringRepository ?: return
        val application = repository.getApplication(postId, applicationId).getOrNull() ?: return
        val role = repository.getPost(postId).getOrNull()?.role?.trim().orEmpty()
        _uiState.update {
            it.copy(
                name = application.name,
                email = application.email.orEmpty(),
                phoneNumber = application.phone.orEmpty(),
                role = role.takeIf { r -> r.isNotEmpty() } ?: it.role
            )
        }
    }

    /** SH-47: the statuses (managers), the dates (owners, financial managers) and the notes. */
    private suspend fun loadStaffDetails(userId: String) {
        val state = _uiState.value
        if (!state.showsStaffStatus) return
        val details = getStaffDetails?.invoke(userId, workplaceId, includeDates = state.showsStaffDates, includeNotes = true)?.getOrNull() ?: return
        loadedStaffRecord = details.record
        _uiState.update {
            it.copy(
                readiness = details.readiness,
                ohsTrainingDate = details.record?.ohsTrainingDate?.toString().orEmpty(),
                ohsValidUntil = details.record?.ohsValidUntil?.toString().orEmpty(),
                medicalValidUntil = details.record?.medicalExamValidUntil?.toString().orEmpty(),
                notes = details.notes.orEmpty()
            )
        }
    }

    /**
     * Saves the dates typed in the dialog. Shown at once (the statuses follow); a failure puts
     * the section back as it was when the dialog opened and says why.
     */
    /** SH-212: the HR dates as loaded, with their history (kept when the form saves the current dates). */
    private var loadedStaffRecord: StaffRecord? = null

    /**
     * SH-212: [base] with the current dates [typed] in the form. A training (or exam) replaced by a
     * later one goes to the history, as the timeline and the OHS course record keep it; a
     * correction of the same one does not. The exam's day stays only while its validity does.
     */
    private fun withTypedDates(base: StaffRecord, typed: StaffRecord): StaffRecord {
        val history = base.history.toMutableList()
        val oldOhs = base.current(com.evoapps.evoshift.domain.model.StaffCheck.OHS_TRAINING)
        val newTraining = typed.ohsTrainingDate
        val oldTraining = oldOhs?.from
        if (oldOhs != null && newTraining != null && oldTraining != null && newTraining > oldTraining) history += oldOhs
        val oldMedical = base.current(com.evoapps.evoshift.domain.model.StaffCheck.MEDICAL_EXAM)
        val oldValidity = oldMedical?.validUntil
        val newMedical = typed.medicalExamValidUntil
        if (oldMedical != null && oldValidity != null && newMedical != null && newMedical > oldValidity) history += oldMedical
        return base.copy(
            ohsTrainingDate = typed.ohsTrainingDate,
            ohsValidUntil = typed.ohsValidUntil,
            medicalExamValidUntil = newMedical,
            medicalExamDate = base.medicalExamDate.takeIf { newMedical == base.medicalExamValidUntil },
            history = history.takeLast(StaffRecord.MAX_HISTORY)
        )
    }

    private fun saveStaffChecks() {
        val state = _uiState.value
        val userId = employeeId ?: return
        val typed = state.typedStaffRecord ?: return
        val use = saveStaffRecord ?: return
        if (!state.canEditStaffDates) return
        if (typed.problems().isNotEmpty()) {
            _uiState.update { it.copy(error = UiText.Resource(Res.string.staff_ohs_valid_before_training)) }
            return
        }
        val before = sectionSnapshot ?: state
        sectionSnapshot = null
        // SH-212: the record as loaded (the exam's day, the timeline's history) with the dates
        // typed; a newer training or exam keeps the one it replaces in the history.
        val record = withTypedDates(loadedStaffRecord ?: StaffRecord(userId, workplaceId), typed).copy(userId = userId, workplaceId = workplaceId)
        _uiState.update { it.copy(readiness = StaffChecks.readiness(record, userId, workplaceId, today())) }
        viewModelScope.launch {
            val editor = getCurrentUserUseCase().first()?.uid.orEmpty()
            use(record, editor).fold(
                onSuccess = {
                    loadedStaffRecord = record
                    AppTelemetry.log(AnalyticsEvent.StaffChecksSaved)
                    _uiState.update { it.copy(message = UiText.Resource(Res.string.staff_checks_saved)) }
                },
                onFailure = { e ->
                    _uiState.update {
                        it.copy(
                            ohsTrainingDate = before.ohsTrainingDate, ohsValidUntil = before.ohsValidUntil,
                            medicalValidUntil = before.medicalValidUntil, readiness = before.readiness,
                            error = if (e is InvalidStaffRecordException) UiText.Resource(Res.string.staff_ohs_valid_before_training) else e.toUiText()
                        )
                    }
                }
            )
        }
    }

    /** Saves the availability notes; optimistic like [saveStaffChecks]. */
    private fun saveNotes() {
        val state = _uiState.value
        val userId = employeeId ?: return
        val use = saveAvailabilityNotes ?: return
        if (!state.canEditNotes) return
        val before = sectionSnapshot ?: state
        sectionSnapshot = null
        _uiState.update { it.copy(notes = state.notes.trim()) }
        viewModelScope.launch {
            val editor = getCurrentUserUseCase().first()?.uid.orEmpty()
            use(userId, workplaceId, state.notes, editor).fold(
                onSuccess = { saved -> _uiState.update { it.copy(notes = saved, message = UiText.Resource(Res.string.staff_notes_saved)) } },
                onFailure = { e -> _uiState.update { it.copy(notes = before.notes, error = e.toUiText()) } }
            )
        }
    }

    fun onEvent(event: EmployeeFormEvent) {
        when (event) {
            is EmployeeFormEvent.UpdateCompetence -> _uiState.update { it.copy(competence = event.value) }
            is EmployeeFormEvent.UpdateOhsTrainingDate -> _uiState.update { it.copy(ohsTrainingDate = event.value) }
            is EmployeeFormEvent.UpdateOhsValidUntil -> _uiState.update { it.copy(ohsValidUntil = event.value) }
            is EmployeeFormEvent.UpdateMedicalValidUntil -> _uiState.update { it.copy(medicalValidUntil = event.value) }
            is EmployeeFormEvent.UpdateNotes -> _uiState.update { it.copy(notes = event.value.take(StaffChecks.NOTES_MAX_LENGTH)) }
            EmployeeFormEvent.SaveStaffChecks -> saveStaffChecks()
            is EmployeeFormEvent.SetWorksUnpaid -> changeWorksUnpaid(event.value)
            is EmployeeFormEvent.SetHiddenInRoster -> changeHiddenInRoster(event.value)
            is EmployeeFormEvent.SetCountsInLabour -> changeCountsInLabour(event.value)
            EmployeeFormEvent.SaveNotes -> saveNotes()
            is EmployeeFormEvent.UpdateName -> _uiState.update { it.copy(name = event.value) }
            is EmployeeFormEvent.UpdateRole -> _uiState.update { it.copy(role = event.value) }
            is EmployeeFormEvent.UpdateEmail -> _uiState.update { it.copy(email = event.value) }
            is EmployeeFormEvent.UpdatePermission -> _uiState.update { it.copy(permission = event.value) }
            is EmployeeFormEvent.UpdateSkills -> _uiState.update { it.copy(skills = event.value) }
            is EmployeeFormEvent.ToggleSkill -> toggleSkill(event.skill)
            is EmployeeFormEvent.UpdateIsPrimary -> _uiState.update { it.copy(isPrimary = event.value) }
            is EmployeeFormEvent.UpdateContractType -> _uiState.update { it.copy(contractType = event.value).withEstimate() }
            is EmployeeFormEvent.UpdateDeclaredNumberOfHours -> _uiState.update {
                it.copy(
                    declaredNumberOfHours = event.value
                ).withEstimate()
            }
            // SH-117: digits only, at most 10 (what the RCP files of the payroll programs take).
            is EmployeeFormEvent.UpdatePayrollId -> _uiState.update {
                it.copy(payrollId = event.value.filter { c -> c in '0'..'9' }.take(PAYROLL_ID_MAX_DIGITS))
            }
            is EmployeeFormEvent.UpdatePhoneNumber -> _uiState.update { it.copy(phoneNumber = event.value) }
            is EmployeeFormEvent.UpdateAddress -> _uiState.update { it.copy(address = event.value) }
            is EmployeeFormEvent.UpdateBankAccount -> _uiState.update { it.copy(bankAccountNumber = event.value) }
            is EmployeeFormEvent.UpdateIsHourly -> _uiState.update { it.copy(isHourly = event.value).withEstimate().withPayDate() }
            is EmployeeFormEvent.UpdateRate -> _uiState.update { it.copy(rate = event.value).withEstimate().withPayDate() }
            is EmployeeFormEvent.UpdateBaseSalary -> _uiState.update { it.copy(baseSalary = event.value).withEstimate().withPayDate() }
            is EmployeeFormEvent.UpdateRateEffectiveFrom -> _uiState.update { it.copy(rateEffectiveFrom = event.value) }
            is EmployeeFormEvent.UpdatePayrollProfile -> _uiState.update { it.copy(payrollProfile = event.value).withEstimate().withPayDate() }
            is EmployeeFormEvent.UpdateFte -> _uiState.update { it.copy(fte = event.value).withEstimate().withPayDate() }
            is EmployeeFormEvent.UpdateShouldSendInvite -> _uiState.update {
                it.copy(
                    shouldSendInvite = event.value
                )
            }

            EmployeeFormEvent.DismissInviteCode -> _uiState.update {
                it.copy(inviteCode = null, success = if (employeeId == null) true else it.success)
            }

            EmployeeFormEvent.Submit -> submit()
            EmployeeFormEvent.SendInvite -> triggerInvite()
            EmployeeFormEvent.CreateInviteCode -> triggerInvite(byEmail = false)
            // After creating someone the form closes with the dialog; editing someone stays.
            EmployeeFormEvent.DismissInviteDialog -> _uiState.update {
                it.copy(showInviteDialogAfterCreation = false, inviteError = null, success = if (employeeId == null) true else it.success)
            }
            EmployeeFormEvent.ShowInviteOptions -> if (_uiState.value.capabilities.shows(Gate.TEAM_EDITING)) {
                _uiState.update { it.copy(showInviteDialogAfterCreation = true, inviteError = null) }
            }
            EmployeeFormEvent.RemoveEmployee -> removeEmployee()
            EmployeeFormEvent.ClearMessage -> _uiState.update {
                it.copy(
                    message = null,
                    error = null
                )
            }
            EmployeeFormEvent.BeginSectionEdit -> sectionSnapshot = _uiState.value
            EmployeeFormEvent.CancelSectionEdit -> {
                sectionSnapshot?.let { before -> _uiState.update { it.withFormOf(before) } }
                sectionSnapshot = null
            }
            EmployeeFormEvent.PlanLimitShown -> _uiState.update { it.copy(planLimit = null) }
            is EmployeeFormEvent.PickPersonFromOtherWorkplace -> pickPerson(event.userId)
            EmployeeFormEvent.ClearPickedPerson -> clearPickedPerson()
        }
    }

    /** This state with the editable fields (and the estimate that follows them) of [other]. */
    private fun EmployeeFormUiState.withFormOf(other: EmployeeFormUiState) = copy(
        name = other.name,
        role = other.role,
        email = other.email,
        permission = other.permission,
        skills = other.skills,
        isPrimary = other.isPrimary,
        competence = other.competence,
        ohsTrainingDate = other.ohsTrainingDate,
        ohsValidUntil = other.ohsValidUntil,
        medicalValidUntil = other.medicalValidUntil,
        notes = other.notes,
        contractType = other.contractType,
        payrollProfile = other.payrollProfile,
        declaredNumberOfHours = other.declaredNumberOfHours,
        payrollId = other.payrollId,
        phoneNumber = other.phoneNumber,
        address = other.address,
        bankAccountNumber = other.bankAccountNumber,
        isHourly = other.isHourly,
        rate = other.rate,
        baseSalary = other.baseSalary,
        fte = other.fte,
        rateEffectiveFrom = other.rateEffectiveFrom,
        estimate = other.estimate,
        estimateHours = other.estimateHours,
        // SH-266: the minimum-wage explanation belongs to the restored estimate.
        estimateFactors = other.estimateFactors
    )

    private fun toggleSkill(skill: String) {
        _uiState.update {
            val currentSkills = it.skills.toMutableSet()
            if (currentSkills.contains(skill)) {
                currentSkills.remove(skill)
            } else {
                currentSkills.add(skill)
            }
            it.copy(skills = currentSkills.toList())
        }
    }

    private fun submit() {
        val state = _uiState.value
        if (state.isSaving || !state.canEdit) return
        if (state.name.isBlank()) {
            _uiState.update { it.copy(error = UiText.Resource(Res.string.employee_name_required)) }
            return
        }
        // SH-148: the form opened again while its previous save runs: no second save.
        if (saves.isPending(saveKey)) {
            _uiState.update { it.copy(error = UiText.Resource(Res.string.saves_in_progress)) }
            return
        }

        _uiState.update { it.copy(isSaving = true) }
        val rateValue = state.rate.toDoubleOrNull() ?: 0.0
        val baseSalaryValue = state.baseSalary.toDoubleOrNull() ?: 0.0
        val fteValue = state.fte.toDoubleOrNull() ?: 1.0
        val declaredHours = state.declaredNumberOfHours.toIntOrNull()
        val previous = loadedFinancial

        val picked = state.pickedPerson
        if (employeeId == null && picked != null) {
            addFromOtherWorkplace(state, picked, rateValue, baseSalaryValue, fteValue, declaredHours)
        } else if (employeeId == null) {
            viewModelScope.launch {
                // CREATE (within the owner's plan)
                planLimitForNewEmployee()?.let { limit ->
                    _uiState.update { it.copy(isSaving = false, planLimit = limit, planLimitCanChoosePlan = it.capabilities.shows(Gate.PLAN)) }
                    return@launch
                }
                // SH-148: in the app's scope: leaving the form does not stop it halfway.
                // SH-186: offline, the person is in this device's cache after a few seconds: the
                // form closes (the invitation can be sent from their page once connected).
                saves.runOrKeepOnDevice(saveKey, connectivity.isOnline, payload = state, successMessage = UiText.Resource(Res.string.employee_created)) {
                    val workplaceName = getWorkplaceUseCase(workplaceId).map { it.name }.getOrDefault("")
                    createEmployeeUseCase(
                        workplaceId = workplaceId,
                        workplaceName = workplaceName,
                        displayName = state.name,
                        role = state.role,
                        email = state.email.ifBlank { null },
                        hourlyRate = if (state.isHourly) rateValue else null,
                        monthlySalary = if (!state.isHourly) baseSalaryValue else null,
                        payrollProfile = state.payrollProfile,
                        fte = fteValue,
                        permission = state.permission,
                        skills = state.skills,
                        isPrimary = state.isPrimary,
                        competence = state.competence,
                        contractType = state.contractType,
                        declaredNumberOfHours = declaredHours,
                        phoneNumber = state.phoneNumber.ifBlank { null },
                        address = state.address.ifBlank { null },
                        bankAccountNumber = state.bankAccountNumber.ifBlank { null },
                        startDate = null,
                        includeSensitive = state.showsPayAndPrivateData,
                        payrollId = state.payrollId
                    ).onSuccess {
                        // SH-147: the missing-pay warnings and labour cost screens read again.
                        if (state.showsPayAndPrivateData) payTermsChanges?.changed()
                        // SH-125: the applicant is now in the team (best effort: the person exists either way).
                        applicant?.let { (postId, applicationId) ->
                            getCurrentUserUseCase().first()?.uid?.let { me -> markApplicantHired?.invoke(postId, applicationId, me) }
                        }
                    }
                }.fold(
                    onSuccess = { newUserId ->
                        _uiState.update {
                            it.copy(
                                isSaving = false,
                                createdUserId = newUserId,
                                showInviteDialogAfterCreation = true,
                                message = UiText.Resource(Res.string.employee_created)
                            )
                        }
                    },
                    onFailure = { e ->
                        _uiState.update { it.copy(isSaving = false, error = e.toUiText()) }
                    },
                    onKept = { _uiState.update { it.copy(isSaving = false, success = true) } }
                )
            }
        } else {
            // UPDATE. SH-57: changed pay applies from the day picked (today when none is);
            // SH-149: so do the payroll facts changed with it.
            val termsFrom = if (state.termsChanged) parseIsoDate(state.rateEffectiveFrom.trim()) ?: today() else null
            val payFrom = termsFrom?.takeIf { state.payChanged }
            // Pricing reads everyone's rates: only for those who may (MANAGE_PAY).
            val reprices = state.showsPayAndPrivateData
            val repriceFrom = listOfNotNull(termsFrom, state.knownEndChangedFrom).minOrNull()?.takeIf { it < today() }
            viewModelScope.launch {
                // SH-148: the save is the write of the person (pay terms included) and nothing
                // more; it runs in the app's scope, so leaving the form cannot stop it halfway.
                saves.runOrKeepOnDevice(saveKey, connectivity.isOnline, payload = state, successMessage = UiText.Resource(Res.string.employee_updated)) {
                    val workplaceName = getWorkplaceUseCase(workplaceId).map { it.name }.getOrDefault("")
                    updateEmployeeUseCase(
                        userId = employeeId,
                        workplaceId = workplaceId,
                        workplaceName = workplaceName,
                        displayName = state.name,
                        role = state.role,
                        email = state.email.ifBlank { null },
                        hourlyRate = if (state.isHourly) rateValue else null,
                        monthlySalary = if (!state.isHourly) baseSalaryValue else null,
                        payrollProfile = state.payrollProfile,
                        fte = fteValue,
                        permission = state.permission,
                        skills = state.skills,
                        isPrimary = state.isPrimary,
                        competence = state.competence,
                        contractTypeId = state.contractType,
                        declaredNumberOfHours = declaredHours,
                        phoneNumber = state.phoneNumber.ifBlank { null },
                        address = state.address.ifBlank { null },
                        bankAccountNumber = state.bankAccountNumber.ifBlank { null },
                        includeSensitive = state.showsPayAndPrivateData,
                        previous = previous,
                        rateEffectiveFrom = payFrom?.toString(),
                        profileEffectiveFrom = termsFrom?.takeIf { state.profileChanged }?.toString(),
                        payrollId = state.payrollId
                    ).onSuccess {
                        // SH-57: new rates apply from the day picked (today by default); shifts
                        // before it keep their cost. Not part of the save's wait (SH-148).
                        if (reprices) startRepricing(repriceFrom)
                    }
                }.fold(
                    onSuccess = {
                        // SH-148 (owner): a section saved from the person's page keeps the page open
                        // with the saved values read back; only adding a person goes back.
                        sectionSnapshot = null
                        _uiState.update { it.copy(isSaving = false, message = UiText.Resource(Res.string.employee_updated)) }
                        loadData(quiet = true)
                    },
                    onFailure = { e ->
                        _uiState.update { it.copy(isSaving = false, error = e.toUiText()) }
                    },
                    // SH-186: kept on this device: the page stays with the values as typed.
                    onKept = {
                        sectionSnapshot = null
                        _uiState.update { it.copy(isSaving = false) }
                    }
                )
            }
        }
    }
    
    /**
     * SH-192: adds this workplace to [picked]'s profile with the form's role, permission and
     * (for those who may) pay terms, in one write. Someone already working for this workplace's
     * owner takes no new seat (the plan counts people per owner), anyone else one, as a new person.
     */
    private fun addFromOtherWorkplace(
        state: EmployeeFormUiState,
        picked: OtherWorkplacePerson,
        rateValue: Double,
        baseSalaryValue: Double,
        fteValue: Double,
        declaredHours: Int?
    ) {
        val add = addEngagement ?: return _uiState.update { it.copy(isSaving = false) }
        viewModelScope.launch {
            planLimitForNewEmployee(adding = if (picked.takesNewSeat) 1 else 0)?.let { limit ->
                _uiState.update { it.copy(isSaving = false, planLimit = limit, planLimitCanChoosePlan = it.capabilities.shows(Gate.PLAN)) }
                return@launch
            }
            val message = UiText.Resource(Res.string.employee_added_from_other_workplace, picked.user.displayName)
            saves.runOrKeepOnDevice(saveKey, connectivity.isOnline, payload = state, successMessage = message) {
                val workplaceName = getWorkplaceUseCase(workplaceId).map { it.name }.getOrDefault("")
                add(
                    userId = picked.user.uid,
                    workplaceId = workplaceId,
                    workplaceName = workplaceName,
                    roles = state.role,
                    skills = state.skills,
                    permission = state.permission,
                    isPrimary = state.isPrimary,
                    competence = state.competence,
                    // Pay terms only from those who may write them (as for a new person).
                    financial = if (state.showsPayAndPrivateData) newFinancialEngagement(
                        contractType = state.contractType,
                        hourlyRate = if (state.isHourly) rateValue else null,
                        monthlySalary = if (!state.isHourly) baseSalaryValue else null,
                        fte = fteValue,
                        declaredNumberOfHours = declaredHours,
                        payrollProfile = state.payrollProfile,
                        payrollId = state.payrollId
                    ) else null
                ).onSuccess {
                    if (state.showsPayAndPrivateData) payTermsChanges?.changed()
                }
            }.fold(
                // They have their account (or invitation) already: the form goes back to the team.
                onSuccess = { _uiState.update { it.copy(isSaving = false, success = true, message = message) } },
                onFailure = { e -> _uiState.update { it.copy(isSaving = false, error = e.toUiText()) } },
                onKept = { _uiState.update { it.copy(isSaving = false, success = true) } }
            )
        }
    }

    /**
     * SH-147: [unpaid] stored in the workplace's payroll settings at once (optimistic: the switch
     * moves, and moves back with the reason when the save fails).
     */
    private fun changeWorksUnpaid(unpaid: Boolean) {
        val useCase = setWorksUnpaid ?: return
        val userId = employeeId ?: return
        val before = _uiState.value
        if (!before.canChangeWorksUnpaid || before.worksUnpaid == unpaid) return
        _uiState.update { it.copy(worksUnpaid = unpaid) }
        viewModelScope.launch {
            useCase(workplaceId, userId, unpaid).fold(
                // SH-206: working without pay changes who counts in the labour figures by default.
                onSuccess = { if (_uiState.value.labourInclusion != null) loadLabourInclusion(userId) },
                onFailure = { e -> _uiState.update { it.copy(worksUnpaid = before.worksUnpaid, error = e.toUiText()) } }
            )
        }
    }

    /** SH-255: whether the person is hidden in the roster for the others (best effort: not shown when unreadable). */
    private suspend fun loadHiddenInRoster(userId: String) {
        val read = getHiddenInRoster ?: return
        if (setHiddenInRoster == null) return
        read(workplaceId, userId).onSuccess { hidden -> _uiState.update { it.copy(hiddenInRoster = hidden) } }
    }

    /**
     * SH-255: [hidden] saved at once through the server (optimistic: the switch moves, and moves back
     * with the reason when the save fails).
     */
    private fun changeHiddenInRoster(hidden: Boolean) {
        val useCase = setHiddenInRoster ?: return
        val userId = employeeId ?: return
        val before = _uiState.value
        if (!before.canChangeHiddenInRoster || before.hiddenInRoster == hidden) return
        _uiState.update { it.copy(hiddenInRoster = hidden) }
        viewModelScope.launch {
            useCase(workplaceId, userId, hidden).onFailure { e ->
                _uiState.update { it.copy(hiddenInRoster = before.hiddenInRoster, error = e.toUiText()) }
            }
        }
    }

    /** SH-206: the switch's state this month (best effort: not shown when it cannot be read). */
    private suspend fun loadLabourInclusion(userId: String) {
        val read = getLabourInclusion ?: return
        if (setLabourInclusion == null) return
        read(workplaceId, userId).onSuccess { status -> _uiState.update { it.copy(labourInclusion = status) } }
    }

    /**
     * SH-206: [counts] saved at once from this month on (optimistic: the switch moves, and moves
     * back with the reason when the save fails); the labour cost screens read again.
     */
    private fun changeCountsInLabour(counts: Boolean) {
        val useCase = setLabourInclusion ?: return
        val userId = employeeId ?: return
        val before = _uiState.value
        val current = before.labourInclusion ?: return
        if (!before.canChangeLabourInclusion || current.counts == counts) return
        _uiState.update { it.copy(labourInclusion = current.copy(counts = counts, isDefault = false)) }
        viewModelScope.launch {
            val me = getCurrentUserUseCase().first()?.uid.orEmpty()
            useCase(workplaceId, userId, counts, me).fold(
                onSuccess = { saved -> _uiState.update { it.copy(labourInclusion = saved) } },
                onFailure = { e -> _uiState.update { it.copy(labourInclusion = current, error = e.toUiText()) } }
            )
        }
    }

    private fun removeEmployee() {
        val targetId = employeeId ?: return
        if (_uiState.value.isSaving || !_uiState.value.canEdit) return
        _uiState.update { it.copy(isSaving = true) }
        viewModelScope.launch {
            terminateEngagementUseCase(userId = targetId, workplaceId = workplaceId).fold(
                onSuccess = {
                    _uiState.update { it.copy(isSaving = false, success = true, message = UiText.Resource(Res.string.employee_removed)) }
                },
                onFailure = { e ->
                    _uiState.update { it.copy(isSaving = false, error = e.toUiText()) }
                }
            )
        }
    }

    /** Creates the invitation; [byEmail] also queues the email and needs an address. */
    private fun triggerInvite(byEmail: Boolean = true) {
        val email = if (byEmail) _uiState.value.email else ""
        if (byEmail && email.isBlank()) {
            _uiState.update { it.copy(error = UiText.Resource(Res.string.employee_email_required)) }
            return
        }
        val targetUserId = employeeId ?: _uiState.value.createdUserId ?: return
        if (_uiState.value.isSaving || !_uiState.value.capabilities.shows(Gate.TEAM_EDITING)) return

        _uiState.update { it.copy(isSaving = true, inviteError = null) }
        viewModelScope.launch {
            val workplaceName =
                getWorkplaceUseCase(workplaceId).map { it.name }.getOrDefault("")
            sendInvite(email, workplaceName, targetUserId)
        }
    }

    private suspend fun sendInvite(email: String, workplaceName: String, targetUserId: String) {
        val manager = getCurrentUserUseCase().first()
        if (manager == null) {
            _uiState.update {
                it.copy(
                    isSaving = false,
                    error = UiText.Resource(Res.string.error_session_expired),
                    inviteError = UiText.Resource(Res.string.error_session_expired)
                )
            }
            return
        }

        sendInviteUseCase(
            email = email,
            workplaceName = workplaceName,
            workplaceId = workplaceId,
            invitedByManagerName = manager.displayName,
            invitedByManagerId = manager.uid,
            targetShadowUserId = targetUserId
        ).fold(
            onSuccess = { code ->
                // Show the code first; a new employee's form closes when the dialog is dismissed.
                _uiState.update {
                    it.copy(isSaving = false, showInviteDialogAfterCreation = false, inviteCode = code)
                }
            },
            onFailure = { e ->
                _uiState.update {
                    it.copy(
                        isSaving = false,
                        error = UiText.Resource(Res.string.error_send_failed, e.toUiText()),
                        inviteError = UiText.Resource(Res.string.error_send_failed, e.toUiText())
                    )
                }
            }
        )
    }
}

/** SH-148: the key of a person's save in [PendingSaves] (a new person: "new"). */
fun employeeSaveKey(workplaceId: String, employeeId: String?): String = "employee:$workplaceId:${employeeId ?: "new"}"

/** SH-148: the key of the background repricing of a workplace's shifts after a pay change. */
fun repriceSaveKey(workplaceId: String): String = "reprice:$workplaceId"
